import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/site/Reveal";
import { useSeo } from "../lib/seo";
import { BOOKING_URL } from "../lib/site";
import { ArrowUpRight } from "lucide-react";
import { NOTFOUND } from "../constants/testIds";

export default function NotFound() {
  useSeo({
    title: "Page not found | Alae Dental",
    description:
      "This page does not seem to exist. Let's get you back to something useful.",
    path: "/404",
  });

  // 404s must never be indexed, and must not claim a canonical URL of their own
  // (a self-canonical on a soft 404 invites Google to keep the URL in the index).
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const created = !el;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");

    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.remove();

    return () => {
      if (created) el.remove();
    };
  }, []);

  return (
    <main data-testid={NOTFOUND.root} className="bg-linen text-umber min-h-[70vh] flex items-center paper-grain">
      <div className="max-w-3xl mx-auto w-full px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <Reveal>
          <div data-testid={NOTFOUND.eyebrow} className="font-meta text-forest mb-6">Error 404</div>
        </Reveal>
        <Reveal delay={100}>
          <h1 data-testid={NOTFOUND.heading} className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-umber">
            This page seems to have{" "}
            <span className="font-display-italic">wandered off.</span>
          </h1>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-umber/85 max-w-xl">
            Not to worry. Everything else is right where we left it. Head back
            to the home page, or book a visit if that&rsquo;s what brought you
            here.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              data-testid={NOTFOUND.returnHome}
              className="bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300 text-center"
            >
              Return home
            </Link>
            <a
              href={BOOKING_URL}
              data-testid={NOTFOUND.book}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-umber/40 text-umber rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-all duration-300 text-center inline-flex items-center justify-center gap-2"
            >
              Book an Appointment <ArrowUpRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
